import {
  LineChart, Landmark, MonitorSmartphone, BrainCircuit,
  TrendingUp, Banknote, Bot, Globe, BarChart3,
} from "lucide-react";

// Homepage: "¿En qué podemos ayudarte?"
export const helpTopics = [
  { id: "crecer", icon: TrendingUp, title: "Hacer crecer mi empresa", desc: "Encontrar nuevas vías de crecimiento y ordenar el negocio para escalar con solidez." },
  { id: "financiacion", icon: Banknote, title: "Mejorar mi financiación", desc: "Sanear la tesorería, los márgenes y el acceso a financiación para invertir con confianza." },
  { id: "automatizar", icon: Bot, title: "Automatizar procesos", desc: "Liberar horas de trabajo repetitivo automatizando las tareas del día a día." },
  { id: "digitalizar", icon: Globe, title: "Digitalizar mi negocio", desc: "Modernizar herramientas y procesos para trabajar mejor y con datos fiables." },
  { id: "rentabilidad", icon: BarChart3, title: "Mejorar la rentabilidad", desc: "Detectar dónde se pierde dinero y convertir esas fugas en beneficio." },
];

// Services (4 áreas)
export const pillars = [
  {
    id: "estrategia",
    icon: LineChart,
    title: "Estrategia Empresarial",
    tagline: "Dirección con criterio",
    desc: "Definimos hacia dónde va tu empresa y cómo llegar, con un plan claro y realista.",
    problem: "El negocio funciona, pero las decisiones se toman sobre la marcha y no está claro cuál es el siguiente paso para crecer.",
    solution: "Analizamos tu empresa, tu mercado y tus números, y construimos contigo un plan sencillo y priorizado, con objetivos y responsables definidos.",
    impact: "Una hoja de ruta que puedes seguir, con metas medibles de ventas, margen y crecimiento.",
    image: "https://images.pexels.com/photos/6950031/pexels-photo-6950031.jpeg",
  },
  {
    id: "financieras",
    icon: Landmark,
    title: "Soluciones Financieras",
    tagline: "Rentabilidad visible",
    desc: "Ponemos orden en las cuentas para que ganes más y duermas tranquilo.",
    problem: "Vendes más pero no lo notas en la caja, y los números llegan tarde para poder decidir con tiempo.",
    solution: "Revisamos tus márgenes, tu tesorería y tus costes, y ponemos en marcha una previsión clara con los indicadores que de verdad importan.",
    impact: "Más beneficio, mejor control de la caja y decisiones tomadas con información fiable y a tiempo.",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71",
  },
  {
    id: "digital",
    icon: MonitorSmartphone,
    title: "Transformación Digital",
    tagline: "Operaciones sin fricción",
    desc: "Modernizamos tus herramientas y procesos para que trabajar sea más fácil.",
    problem: "Demasiadas tareas manuales, herramientas que no se hablan entre sí y datos en los que no se puede confiar.",
    solution: "Ordenamos tus procesos clave, elegimos las herramientas adecuadas y las conectamos en torno a una única fuente de información.",
    impact: "Menos tiempo perdido, menos errores y un negocio preparado para crecer sin perder el control.",
    image: "https://images.pexels.com/photos/8134173/pexels-photo-8134173.jpeg",
  },
  {
    id: "ia",
    icon: BrainCircuit,
    title: "Inteligencia Artificial y Automatización",
    tagline: "Tecnología a tu favor",
    desc: "Aplicamos IA y automatización de forma práctica, sin complicaciones.",
    problem: "Se habla mucho de inteligencia artificial, pero no está claro cómo aplicarla de forma útil y segura en tu empresa.",
    solution: "Identificamos las tareas que más tiempo consumen, las automatizamos y ponemos la IA a trabajar en tu día a día con las garantías necesarias.",
    impact: "Horas recuperadas cada semana para tu equipo y decisiones apoyadas por la tecnología.",
    image: "https://images.unsplash.com/photo-1609915189900-455ffd52e50f?crop=entropy&cs=srgb&fm=jpg&q=85",
  },
];

// Nuestra metodología
export const framework = [
  { step: "01", name: "Analizamos", desc: "Nos sentamos contigo a entender tu empresa: cómo funciona, sus números y sus retos, sin dar nada por supuesto." },
  { step: "02", name: "Diagnosticamos", desc: "Identificamos qué está frenando de verdad al negocio y dónde están las oportunidades reales de mejora." },
  { step: "03", name: "Diseñamos", desc: "Preparamos un plan a tu medida, sencillo y priorizado, con pasos claros y objetivos concretos." },
  { step: "04", name: "Implementamos", desc: "Nos remangamos y trabajamos junto a tu equipo para poner el plan en marcha, no solo para recomendarlo." },
  { step: "05", name: "Mejoramos continuamente", desc: "Medimos los resultados, ajustamos lo que haga falta y seguimos a tu lado para que el negocio siga mejorando." },
];

// ¿Por qué Medite Group?
export const whyUs = [
  { n: "01", title: "Cada empresa es diferente", text: "Escuchamos y entendemos tu negocio antes de proponer nada. Ninguna empresa es igual a otra, y así la tratamos." },
  { n: "02", title: "No existen soluciones genéricas", text: "Huimos de las recetas de manual. Diseñamos soluciones a la medida de tu realidad, tu equipo y tu ambición." },
  { n: "03", title: "Primero el diagnóstico", text: "Cada recomendación parte de un diagnóstico previo del negocio. Nunca actuamos sin haber entendido antes el porqué." },
  { n: "04", title: "Especialistas coordinados", text: "Estrategia, finanzas, tecnología e inteligencia artificial trabajando como un solo equipo, bajo una misma visión." },
  { n: "05", title: "Resultados medibles", text: "Nos comprometemos con resultados concretos y los medimos, para que siempre sepas qué estás consiguiendo." },
];

export const industries = [
  "Restauración", "Talleres", "Clínicas", "Despachos", "Comercio",
  "Construcción", "Industria", "Logística", "Ecommerce", "Servicios",
];

export const clientProcess = [
  { k: "Primera conversación", v: "Una charla cercana para conocer tu empresa, tus retos y lo que quieres conseguir." },
  { k: "Diagnóstico inicial", v: "Analizamos tu negocio y te entregamos una foto clara de oportunidades y prioridades." },
  { k: "Propuesta a medida", v: "Un plan de trabajo concreto, con objetivos, pasos y las mejoras que vamos a buscar juntos." },
  { k: "Implementación y mejora", v: "Trabajamos a tu lado para poner el plan en marcha y seguimos midiendo y mejorando." },
];

export const faqs = [
  { q: "¿Con qué tipo de empresas trabajáis?", a: "Trabajamos con pequeñas y medianas empresas de todos los sectores: restaurantes, talleres, clínicas, despachos, comercios, construcción, industria o ecommerce, entre otros. Si tienes un negocio y quieres mejorarlo, podemos ayudarte." },
  { q: "¿Solo asesoráis o también ayudáis a implantar?", a: "Ambas cosas. No nos limitamos a recomendar: trabajamos junto a tu equipo para poner en marcha las soluciones y acompañarte en el día a día." },
  { q: "¿Cómo empieza un proyecto con vosotros?", a: "Siempre empezamos con un diagnóstico inicial. Analizamos tu empresa, detectamos oportunidades y, a partir de ahí, te proponemos un plan de trabajo a medida." },
  { q: "¿Necesito saber de tecnología o de finanzas?", a: "No. Nuestro trabajo es traducir todo a un lenguaje claro y práctico. Te explicamos cada paso para que tomes decisiones con total confianza." },
  { q: "¿Cuánto tardamos en empezar a ver resultados?", a: "Depende de cada empresa, pero durante el propio diagnóstico ya solemos identificar mejoras que se pueden aplicar desde el primer momento." },
];

export const insights = [
  { category: "Estrategia", title: "Cómo saber si tu empresa está lista para crecer", excerpt: "Cinco señales que indican que ha llegado el momento de dar el siguiente paso, y cómo prepararse para hacerlo con solidez." },
  { category: "Finanzas", title: "Vender más no siempre es ganar más", excerpt: "Por qué el beneficio y la caja no siempre acompañan al crecimiento, y qué mirar para no llevarte sorpresas." },
  { category: "Tecnología", title: "Inteligencia artificial para pymes, sin humo", excerpt: "Casos prácticos en los que la automatización y la IA ya ahorran horas de trabajo a negocios como el tuyo." },
];
